import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { focusMatchConfigApi } from '../api';
import type {
  BulkUpdateFocusMatchSchedulesPayload,
  FocusMatchLiveOps,
  FocusMatchRule,
  FocusMatchSchedule,
  MatchesByStage,
  UpdateFocusMatchRulePayload,
} from '../types';
import { getErrorMessage } from '../utils';

type State = {
  liveOps: FocusMatchLiveOps | null;
  schedules: FocusMatchSchedule[];
  rule: FocusMatchRule | null;
  matchesByStage: MatchesByStage | null;
};

const REFRESH_INTERVAL = 30 * 1000;

export const useFocusMatchConfig = () => {
  const mountedRef = useRef(true);
  const abortRef = useRef<AbortController | null>(null);

  const [data, setData] = useState<State>({
    liveOps: null,
    schedules: [],
    rule: null,
    matchesByStage: null,
  });

  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [liveOpsRefreshing, setLiveOpsRefreshing] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchLiveOps = useCallback(async (options?: { silent?: boolean }) => {
    if (options?.silent) {
      setLiveOpsRefreshing(true);
    }

    try {
      const liveOps = await focusMatchConfigApi.getLiveOps();

      if (mountedRef.current) {
        setData((prev) => ({
          ...prev,
          liveOps,
        }));
      }
    } catch (err) {
      if (mountedRef.current && !options?.silent) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLiveOpsRefreshing(false);
      }
    }
  }, []);

  const fetchAll = useCallback(async (options?: { silent?: boolean }) => {
    abortRef.current?.abort();

    const controller = new AbortController();
    abortRef.current = controller;

    if (options?.silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }

    setError(null);

    try {
      const [liveOpsResult, schedulesResult, ruleResult, matchesResult] = await Promise.allSettled([
        focusMatchConfigApi.getLiveOps(controller.signal),
        focusMatchConfigApi.getSchedules(controller.signal),
        focusMatchConfigApi.getRule(controller.signal),
        focusMatchConfigApi.getAvailableMatches(controller.signal),
      ]);

      if (!mountedRef.current) return;

      setData((prev) => ({
        liveOps: liveOpsResult.status === 'fulfilled' ? liveOpsResult.value : prev.liveOps,
        schedules:
          schedulesResult.status === 'fulfilled' && Array.isArray(schedulesResult.value)
            ? schedulesResult.value
            : prev.schedules,
        rule: ruleResult.status === 'fulfilled' ? ruleResult.value : prev.rule,
        matchesByStage:
          matchesResult.status === 'fulfilled' ? matchesResult.value : prev.matchesByStage,
      }));

      const failed = [liveOpsResult, schedulesResult, ruleResult, matchesResult].find(
        (item) => item.status === 'rejected'
      );

      if (failed && failed.status === 'rejected') {
        setError(new Error(getErrorMessage(failed.reason)));
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(new Error(getErrorMessage(err)));
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, []);

  const bulkUpdateSchedules = useCallback(
    async (payload: BulkUpdateFocusMatchSchedulesPayload) => {
      setMutating(true);

      try {
        return await focusMatchConfigApi.bulkUpdateSchedules(payload);
      } finally {
        if (mountedRef.current) {
          setMutating(false);
        }
      }
    },
    []
  );

  const updateRule = useCallback(async (payload: UpdateFocusMatchRulePayload) => {
    setMutating(true);

    try {
      return await focusMatchConfigApi.updateRule(payload);
    } finally {
      if (mountedRef.current) {
        setMutating(false);
      }
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;

    fetchAll();

    const timer = window.setInterval(() => {
      fetchLiveOps({ silent: true });
    }, REFRESH_INTERVAL);

    return () => {
      mountedRef.current = false;
      abortRef.current?.abort();
      window.clearInterval(timer);
    };
  }, [fetchAll, fetchLiveOps]);

  return useMemo(
    () => ({
      ...data,
      loading,
      refreshing,
      liveOpsRefreshing,
      mutating,
      error,
      refetchAll: () => fetchAll({ silent: true }),
      refreshLiveOps: () => fetchLiveOps({ silent: true }),
      bulkUpdateSchedules,
      updateRule,
    }),
    [
      data,
      loading,
      refreshing,
      liveOpsRefreshing,
      mutating,
      error,
      fetchAll,
      fetchLiveOps,
      bulkUpdateSchedules,
      updateRule,
    ]
  );
};
